import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Check, 
  X, 
  AlertTriangle, 
  CreditCard, 
  Calendar,
  Clock,
  ExternalLink,
  Loader2,
  Ban
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { siteData } from '../data/mock';
import { formatPrice } from '../data/orcamentoMock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: {
    label: 'Aguardando Pagamento',
    color: 'yellow',
    icon: Clock,
    description: 'Realize o pagamento para ativar sua assinatura'
  },
  active: {
    label: 'Ativa',
    color: 'green',
    icon: Check,
    description: 'Sua assinatura está ativa e funcionando'
  },
  overdue: {
    label: 'Pagamento Atrasado',
    color: 'orange',
    icon: AlertTriangle,
    description: 'Regularize o pagamento para evitar suspensão'
  },
  suspended: {
    label: 'Suspensa',
    color: 'red',
    icon: Ban,
    description: 'Assinatura suspensa por falta de pagamento'
  },
  blocked: {
    label: 'Bloqueada',
    color: 'gray',
    icon: X,
    description: 'Entre em contato para reativar sua assinatura'
  },
  cancelled: {
    label: 'Cancelada',
    color: 'gray',
    icon: X,
    description: 'Assinatura cancelada'
  }
};

const GerenciarAssinatura = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [canCancel, setCanCancel] = useState(false);
  const [error, setError] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    loadSubscription();
  }, [slug]);

  const loadSubscription = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscriptions/manage/${slug}`);
      setSubscription(response.data.subscription);
      setPayments(response.data.payments || []);
      setCanCancel(response.data.can_cancel);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Assinatura não encontrada');
      } else {
        setError('Erro ao carregar assinatura');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/subscriptions/cancel/${slug}`, null, {
        params: { reason: cancelReason || 'Cancelado pelo cliente' }
      });
      await loadSubscription();
      setShowCancelConfirm(false);
    } catch (err) {
      alert('Erro ao cancelar assinatura');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <X size={48} className="text-red-500 mb-4" />
        <h1 className="text-xl font-bold text-slate-900 mb-2">{error}</h1>
        <Button onClick={() => navigate('/')} variant="outline">
          <ArrowLeft size={18} />
          Voltar ao início
        </Button>
      </div>
    );
  }

  const status = subscription?.status || 'pending';
  const config = statusConfig[status] || statusConfig.pending;
  const StatusIcon = config.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <img src={siteData.brand.logo} alt="AtivaJá" className="h-8" />
            </a>
            <span className="text-sm text-slate-500">Gerenciar Assinatura</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Status Card */}
        <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden mb-6 ${
          config.color === 'green' ? 'border-green-200' :
          config.color === 'yellow' ? 'border-yellow-200' :
          config.color === 'orange' ? 'border-orange-200' :
          config.color === 'red' ? 'border-red-200' :
          'border-slate-200'
        }`}>
          <div className={`p-4 ${
            config.color === 'green' ? 'bg-green-50' :
            config.color === 'yellow' ? 'bg-yellow-50' :
            config.color === 'orange' ? 'bg-orange-50' :
            config.color === 'red' ? 'bg-red-50' :
            'bg-slate-50'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                config.color === 'green' ? 'bg-green-100' :
                config.color === 'yellow' ? 'bg-yellow-100' :
                config.color === 'orange' ? 'bg-orange-100' :
                config.color === 'red' ? 'bg-red-100' :
                'bg-slate-100'
              }`}>
                <StatusIcon size={20} className={`${
                  config.color === 'green' ? 'text-green-600' :
                  config.color === 'yellow' ? 'text-yellow-600' :
                  config.color === 'orange' ? 'text-orange-600' :
                  config.color === 'red' ? 'text-red-600' :
                  'text-slate-600'
                }`} />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{config.label}</p>
                <p className="text-sm text-slate-600">{config.description}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-1">
              {subscription?.business_name}
            </h2>
            <p className="text-slate-500 mb-4">{subscription?.plan_name}</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Valor mensal</p>
                <p className="text-lg font-bold text-slate-900">
                  {formatPrice(subscription?.plan_value || 29.99)}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Seu link</p>
                <p className="text-sm font-medium text-blue-600 truncate">
                  /orcamento/{subscription?.slug}
                </p>
              </div>
            </div>

            {/* Action buttons based on status */}
            {status === 'pending' && subscription?.payment_link && (
              <a
                href={subscription.payment_link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl inline-flex items-center justify-center gap-2 transition-colors"
              >
                <CreditCard size={18} />
                Pagar agora
                <ExternalLink size={16} />
              </a>
            )}

            {status === 'overdue' && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-orange-800">
                  <strong>Atenção:</strong> Seu pagamento está atrasado. 
                  Regularize para evitar a suspensão do seu link de orçamentos.
                </p>
              </div>
            )}

            {(status === 'suspended' || status === 'blocked') && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-800 mb-3">
                  Sua assinatura foi {status === 'suspended' ? 'suspensa' : 'bloqueada'} por falta de pagamento.
                  Entre em contato para regularizar.
                </p>
                <a
                  href={siteData.whatsapp.getLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Falar no WhatsApp →
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={18} className="text-slate-400" />
              <span className="text-sm text-slate-500">Criado em</span>
            </div>
            <p className="font-medium text-slate-900">
              {subscription?.created_at 
                ? new Date(subscription.created_at).toLocaleDateString('pt-BR')
                : '-'
              }
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard size={18} className="text-slate-400" />
              <span className="text-sm text-slate-500">Último pagamento</span>
            </div>
            <p className="font-medium text-slate-900">
              {subscription?.last_payment_date 
                ? new Date(subscription.last_payment_date).toLocaleDateString('pt-BR')
                : 'Nenhum pagamento'
              }
            </p>
          </div>
        </div>

        {/* Payment History */}
        {payments.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">Histórico de Pagamentos</h3>
            <div className="space-y-3">
              {payments.map((payment, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {formatPrice(payment.value)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {payment.payment_date 
                        ? new Date(payment.payment_date).toLocaleDateString('pt-BR')
                        : 'Pendente'
                      }
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    payment.status === 'RECEIVED' 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {payment.status === 'RECEIVED' ? 'Pago' : 'Pendente'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cancel Section */}
        {canCancel && !showCancelConfirm && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-2">Cancelar Assinatura</h3>
            <p className="text-sm text-slate-600 mb-4">
              Você pode cancelar sua assinatura a qualquer momento. 
              O cancelamento será efetivado ao final do período pago.
            </p>
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => setShowCancelConfirm(true)}
            >
              Cancelar assinatura
            </Button>
          </div>
        )}

        {/* Cancel Confirmation */}
        {showCancelConfirm && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <h3 className="font-semibold text-red-900 mb-2">Confirmar Cancelamento</h3>
            <p className="text-sm text-red-800 mb-4">
              Tem certeza que deseja cancelar sua assinatura? 
              Seu link de orçamentos será desativado.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm text-red-800 mb-2">
                Motivo do cancelamento (opcional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-red-200 rounded-lg resize-none h-20 text-sm"
                placeholder="Conte-nos o motivo..."
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCancelConfirm(false)}
              >
                Voltar
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleCancel}
                disabled={cancelLoading}
              >
                {cancelLoading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  'Confirmar cancelamento'
                )}
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-xs text-slate-400">
          Dúvidas? <a href={siteData.whatsapp.getLink()} className="text-blue-600 hover:underline">Fale conosco</a>
        </p>
      </footer>
    </div>
  );
};

export default GerenciarAssinatura;
